import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import { InternationalAddress } from '@toasttab/buffet-patterns-international-address'
import {
  Address,
  AddressLookup,
  mapboxPublicToken
} from '@toasttab/buffet-patterns-address-lookup'
import { useFormikContext } from 'formik'
import { TextInput } from '@toasttab/buffet-pui-text-input'
import { normalizeAddress } from '../../utils/address-helpers'
import styles from './AddressAutocomplete.module.css'
import { Label } from '@toasttab/buffet-pui-text-base'

export const AddressAutocomplete = ({ field }) => {
  const { isLoading, isError, restaurantInfo, country } = useRestaurant()
  const { setFieldValue } = useFormikContext()

  const restaurantAddress = restaurantInfo?.address
  const restaurantCoordinates = // != null checks for null or undefined
    restaurantAddress?.latitude != null && restaurantAddress?.longitude != null
      ? {
          latitude: restaurantAddress.latitude,
          longitude: restaurantAddress.longitude
        }
      : undefined

  // Determine if this is an international address (not US or CA)
  const isIntlAddress = country && country !== 'US' && country !== 'CA'

  const handleAddressChange = useCallback(
    (address: Address) => {
      const normalizedAddress = normalizeAddress(address, country)
      setFieldValue(field.name, normalizedAddress)
    },
    [field.name, setFieldValue]
  )

  if (isLoading || isError) return null

  // Normalize across inconsistent address formats
  // Ensure country is set for international addresses
  let address = field.value
  if (address || isIntlAddress) {
    address = normalizeAddress(field.value, country)
  }

  // Use InternationalAddress for non-US/CA countries, AddressLookup for US/CA
  return isIntlAddress ? (
    <InternationalAddress
      testId='AddressAutocomplete'
      defaultValue={address}
      onChange={handleAddressChange}
      lookupServiceAccessToken={mapboxPublicToken}
      containerClassName={`pb-3 ${styles.container}`}
      proximity={restaurantCoordinates}
      includePOI={true}
      required={true}
    />
  ) : (
    <>
      <Label required>Address</Label>
      <AddressLookup
        countries={country === 'CA' ? 'ca' : 'us'}
        proximity={restaurantCoordinates}
        includePOI={true}
        lookupServiceAccessToken={mapboxPublicToken}
        onChange={handleAddressChange}
        placeholder='Enter your delivery address'
        value={address}
        testId='AddressAutocomplete'
      />
      <TextInput
        containerClassName='mt-2 mb-3'
        aria-label='Address line 2'
        onChange={(event) => {
          const updatedAddress = { ...address, address2: event.target.value }
          handleAddressChange(updatedAddress)
        }}
        placeholder={'Apt, suite, floor (optional)'}
        value={address?.address2}
      />
    </>
  )
}

const FieldShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  value: PropTypes.object
})

AddressAutocomplete.propTypes = {
  field: FieldShape.isRequired
}
